import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { usePostHog } from 'posthog-js/react'

import { Sidebar, SidebarContent, useSidebar } from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { useConversationContext } from '@/contexts/ConversationContext'
import { useConversationNav } from '@/hooks/useConversationNav'
import {
  useConversationsList,
  useDeleteConversation,
} from '@/hooks/useConversationsQuery'
import { useAppStore } from '@/stores/app'
import { chatSidebarMaxWidth } from '@/stores/app/chatSidebarSlice'
import {
  CHAT_SIDEBAR_MIN_WIDTH_PX,
  CHAT_SIDEBAR_RESIZE_STEP_PX,
  CHAT_SIDEBAR_RESIZE_STEP_SHIFT_PX,
} from '@/config/constants'

import { ConversationSidebarHeader } from './ConversationSidebarHeader'
import { ConversationHistoryList } from './ConversationHistoryList'
import { ConversationHistorySearch } from './ConversationHistorySearch'

interface ConversationSidebarProps {
  chatComponent?: React.ReactNode
}

export function ConversationSidebar({
  chatComponent,
}: ConversationSidebarProps) {
  const { open, openMobile, isMobile, setOpen } = useSidebar()
  const mobileOpen = isMobile && openMobile
  const posthog = usePostHog()
  const { agentId, discardThread } = useConversationContext()
  const { goToConversation, clearActiveConversation } = useConversationNav()
  const [searchParams] = useSearchParams()
  const viewParam = searchParams.get('view')
  const conversationId = searchParams.get('conversationId') || undefined

  const isOnConversationRoute = !!conversationId
  const isHistoryMode = viewParam === 'history'
  const shouldShowChatUI =
    !isHistoryMode &&
    (isOnConversationRoute ||
      viewParam === 'new' ||
      (!viewParam && !isOnConversationRoute)) &&
    chatComponent

  const { data: conversations = [], isLoading: loading } =
    useConversationsList(agentId)
  const deleteMutation = useDeleteConversation(agentId)

  const [searchQuery, setSearchQuery] = useState('')
  const filteredConversations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return conversations
    return conversations.filter((c) => c.title.toLowerCase().includes(q))
  }, [conversations, searchQuery])

  const sidebarWidth = useAppStore((s) => s.chatSidebar.width)
  const [isDragging, setIsDragging] = useState(false)

  // Listeners are attached inline in mousedown and removed in mouseup — no
  // useEffect needed (per project no-use-effect rule). Lifecycle exactly
  // matches the drag gesture.
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const onMove = (ev: MouseEvent) => {
      useAppStore.getState().setChatSidebarWidth(window.innerWidth - ev.clientX)
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      setIsDragging(false)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const step = e.shiftKey
      ? CHAT_SIDEBAR_RESIZE_STEP_SHIFT_PX
      : CHAT_SIDEBAR_RESIZE_STEP_PX
    const current = useAppStore.getState().chatSidebar.width
    let next: number | null = null
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = current + step
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = current - step
    if (e.key === 'Home') next = CHAT_SIDEBAR_MIN_WIDTH_PX
    if (e.key === 'End') next = chatSidebarMaxWidth()
    if (next != null) {
      e.preventDefault()
      useAppStore.getState().setChatSidebarWidth(next)
    }
  }, [])

  const handleConversationClick = useCallback(
    (convId: string) => {
      posthog?.capture('conversation_switch', { id: convId, agent: agentId })
      goToConversation(agentId, convId)
      if (!open) {
        setOpen(true)
      }
    },
    [posthog, agentId, goToConversation, open, setOpen]
  )

  const handleDeleteConversation = useCallback(
    async (convId: string) => {
      const wasSelected = conversationId === convId
      // Await completion so the confirm dialog can stay open + show a loading
      // state until the row is actually gone (no flash of the deleted chat).
      await deleteMutation.mutateAsync(convId)
      posthog?.capture('conversation_deleted', { id: convId, agent: agentId })
      // Drop any live stream/events belonging to the deleted conversation.
      // They live in ConversationContext (above AgentChat), so they outlive
      // AgentChat's own unmount in history mode — left stale they make the
      // chat view render blank instead of the new-chat empty state.
      discardThread(convId)
      if (wasSelected) {
        // Deletion happens from the history tab. Clear the deleted chat's
        // pointer but stay in history (preserve `view`) so the list remains
        // visible — going back to chat then lands on the new-chat empty state.
        clearActiveConversation(agentId)
      }
    },
    [
      conversationId,
      deleteMutation,
      posthog,
      agentId,
      discardThread,
      clearActiveConversation,
    ]
  )

  return (
    <Sidebar
      className={cn(
        // <768 — this IS the mobile sheet: a full-screen fixed panel over the
        // whole viewport. No backdrop is needed because nothing shows behind
        // it.
        isMobile && !mobileOpen && 'hidden',
        isMobile && mobileOpen && 'flex fixed inset-0 z-40 w-full',
        // 768–1279 — OVERLAY tier. Still viewport-fixed, floating above the
        // content (which now spans the full centre track). Width reads the
        // shell's `--chat-width` var rather than an inline style, so a drag
        // never re-renders this panel for geometry — capped so it always
        // leaves --content-min-visible of canvas showing. 100% (not 100vw)
        // because this panel is `fixed`: its containing block is the viewport,
        // so 100% is the viewport width minus the scrollbar, which is exactly
        // the space the cap is dividing up.
        'md:flex md:fixed md:top-0 md:bottom-0 md:right-0 md:z-50',
        'md:bg-transparent md:rounded-xl',
        'md:w-[min(var(--chat-width),calc(100%_-_var(--app-sidebar-width)_-_var(--content-min-visible)))]',
        // Floating above content needs its own surface + lift; transparent
        // over the gradient only worked while content reserved the width.
        'md:data-[state=open]:bg-app md:data-[state=open]:shadow-xl',
        // Pin the closed rail to an explicit width so the grid track in
        // AuthLayout can match it; without this the closed sidebar's natural
        // width drifts with the SidebarHeader content.
        'md:data-[state=closed]:w-(--app-rail-width)',
        // ≥1280 — INLINE tier. The panel is a real grid child, so it drops
        // out of the fixed layer and simply fills its track (the track, not
        // the panel, applies the 640px centre-width clamp). `relative`, not
        // `static`: the resize handle is absolutely positioned inside it.
        'xl:relative xl:z-auto xl:col-start-3 xl:w-full',
        // Beats the md closed rule.
        'xl:data-[state=closed]:w-full',
        'xl:data-[state=open]:bg-transparent xl:data-[state=open]:shadow-none',
        isDragging && 'transition-none'
      )}
      // Decorative at xl, where the grid track (not this element) animates.
      style={{ willChange: isDragging ? 'width' : undefined }}>
      {/* Desktop resize handle (left edge of right sidebar) */}
      {open && !isMobile && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={sidebarWidth}
          aria-valuemin={CHAT_SIDEBAR_MIN_WIDTH_PX}
          // Same tier-aware ceiling the store enforces, so the announced max
          // is the width a drag can actually reach.
          aria-valuemax={
            typeof window === 'undefined' ? undefined : chatSidebarMaxWidth()
          }
          tabIndex={0}
          className={cn(
            'absolute top-0 left-0 h-full w-px cursor-col-resize transition-colors z-10 group',
            isDragging
              ? 'bg-david-solid'
              : 'bg-transparent hover:bg-david-solid'
          )}
          onMouseDown={handleMouseDown}
          onKeyDown={handleKeyDown}
          title="Resize sidebar"
          aria-label="Resize sidebar">
          <div
            role="presentation"
            className={cn(
              'absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/2 w-3 h-10 bg-david-solid opacity-20 rounded-md flex items-center justify-center transition-opacity',
              'group-hover:opacity-100'
            )}
            onMouseDown={handleMouseDown}>
            <div className="w-0.5 h-5 bg-background mr-0.5 rounded" />
            <div className="w-0.5 h-5 bg-background rounded" />
          </div>
        </div>
      )}

      <ConversationSidebarHeader />

      {(open || mobileOpen) && (
        // Mobile only: the panel is full-screen, but the bottom nav is fixed
        // on top of it, so reserve the nav's height plus the device home
        // indicator inset — otherwise the composer sits under the nav.
        <SidebarContent className="flex flex-col min-h-0 overflow-hidden pb-[calc(var(--bottom-nav-height)+env(safe-area-inset-bottom))] md:pb-0">
          {shouldShowChatUI ? (
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden w-full h-full">
              <div className="flex-1 flex flex-col min-h-0 w-full h-full [&>div]:max-w-none [&>div]:h-full">
                {chatComponent}
              </div>
            </div>
          ) : (
            <>
              <ConversationHistorySearch
                value={searchQuery}
                onChange={setSearchQuery}
              />
              <ConversationHistoryList
                conversations={filteredConversations}
                loading={loading}
                activeConversationId={conversationId}
                searchQuery={searchQuery}
                onConversationClick={handleConversationClick}
                onDeleteConversation={handleDeleteConversation}
              />
            </>
          )}
        </SidebarContent>
      )}
    </Sidebar>
  )
}
